import { useEffect, useState } from 'react';

export default function Hero() {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  const scrollToBooking = () => {
    document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden bg-gradient-to-b from-[#0a0e27] via-[#050810] to-black">
      {/* Animated Background Grid */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute inset-0" style={{
          backgroundImage: 'linear-gradient(rgba(0, 212, 255, 0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 212, 255, 0.1) 1px, transparent 1px)',
          backgroundSize: '100px 100px',
          transform: 'perspective(500px) rotateX(60deg)',
          transformOrigin: 'center bottom'
        }} />
      </div>

      {/* Floating Particles */}
      <div className="absolute inset-0 overflow-hidden">
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            className="absolute w-1 h-1 bg-cyan-400 rounded-full animate-float"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 5}s`,
              animationDuration: `${5 + Math.random() * 10}s`,
              opacity: Math.random() * 0.5 + 0.3,
              filter: 'blur(1px)'
            }}
          />
        ))}
      </div>

      {/* Glowing Orbs */}
      <div className="absolute top-20 left-20 w-96 h-96 bg-cyan-500/20 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-20 w-96 h-96 bg-blue-600/20 rounded-full blur-3xl" />

      <div className="container mx-auto px-6 relative z-10">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Left Content */}
          <div className="flex-1 text-center lg:text-left">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-cyan-400/30 backdrop-blur-sm mb-8 transition-all duration-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <div className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
              <span className="text-xs font-bold text-cyan-400 tracking-wider uppercase">AI-Powered Automation</span>
            </div>

            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              <div className={`transition-all duration-700 delay-100 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <span className="text-white drop-shadow-[0_0_30px_rgba(0,212,255,0.3)]">Automate Your Business.</span>
              </div>
              <div className={`transition-all duration-700 delay-300 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <span className="text-white drop-shadow-[0_0_30px_rgba(0,212,255,0.3)]">Capture More Leads.</span>
              </div>
              <div className={`transition-all duration-700 delay-500 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-blue-600 drop-shadow-[0_0_30px_rgba(0,212,255,0.5)]">Book More Jobs.</span>
              </div>
            </h1>

            <p className={`text-lg md:text-xl text-[#a0b3cc] mb-10 max-w-2xl mx-auto lg:mx-0 transition-all duration-700 delay-700 ${isLoaded ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
              Bayfront Intelligence helps home service businesses with AI voice agents, smart calendars, automated follow-up, websites, and lead systems that work 24/7.
            </p>

            <div className={`flex flex-col sm:flex-row gap-4 justify-center lg:justify-start transition-all duration-700 delay-900 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <button onClick={scrollToBooking} className="group relative px-8 py-4 bg-gradient-to-r from-cyan-400 to-blue-600 text-white font-bold rounded-full overflow-hidden transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(0,212,255,0.6)] whitespace-nowrap cursor-pointer">
                <span className="relative z-10 flex items-center justify-center gap-2">
                  Book a Demo
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-cyan-400 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
              </button>
              <button onClick={scrollToBooking} className="group px-8 py-4 bg-transparent border-2 border-cyan-400 text-white font-bold rounded-full transition-all duration-300 hover:bg-cyan-400/10 hover:shadow-[0_0_30px_rgba(0,212,255,0.4)] whitespace-nowrap cursor-pointer">
                <span className="flex items-center justify-center gap-2">
                  See How It Works
                  <i className="ri-play-circle-line transition-transform group-hover:scale-110" />
                </span>
              </button>
            </div>
          </div>

          {/* Right Visual */}
          <div className={`flex-1 relative transition-all duration-1000 delay-500 ${isLoaded ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-12'}`}>
            <div className="relative">
              {/* Main Dashboard Card */}
              <div className="relative bg-white/5 backdrop-blur-xl border border-cyan-400/30 rounded-3xl p-8 shadow-[0_0_60px_rgba(0,212,255,0.2)]">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 bg-red-500 rounded-full" />
                    <div className="w-3 h-3 bg-yellow-500 rounded-full" />
                    <div className="w-3 h-3 bg-green-500 rounded-full" />
                  </div>
                  <div className="text-xs text-cyan-400 font-mono">AI Dashboard</div>
                </div>
                
                <div className="space-y-4">
                  <div className="bg-gradient-to-r from-cyan-500/10 to-blue-600/10 border border-cyan-400/20 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-cyan-400/20 rounded-full flex items-center justify-center">
                        <i className="ri-phone-line text-cyan-400 text-lg" />
                      </div>
                      <div>
                        <div className="text-white font-semibold text-sm">Incoming Call</div>
                        <div className="text-cyan-400 text-xs">AI Agent Responding...</div>
                      </div>
                    </div>
                    <div className="h-2 bg-black/30 rounded-full overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-cyan-400 to-blue-600 rounded-full animate-pulse" style={{ width: '70%' }} />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                      <div className="text-2xl font-bold text-white mb-1">247</div>
                      <div className="text-xs text-gray-400">Leads Captured</div>
                    </div>
                    <div className="bg-white/5 border border-white/10 rounded-lg p-3">
                      <div className="text-2xl font-bold text-cyan-400 mb-1">89%</div>
                      <div className="text-xs text-gray-400">Response Rate</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating Status Cards */}
              <div className="absolute -top-6 -right-6 bg-gradient-to-br from-green-400/90 to-emerald-600/90 backdrop-blur-xl border border-green-400/50 rounded-2xl px-4 py-3 shadow-[0_0_30px_rgba(34,197,94,0.4)] animate-float">
                <div className="flex items-center gap-2">
                  <i className="ri-checkbox-circle-fill text-white text-xl" />
                  <div>
                    <div className="text-white font-bold text-sm whitespace-nowrap">Lead Captured</div>
                    <div className="text-white/80 text-xs">2 mins ago</div>
                  </div>
                </div>
              </div>

              <div className="absolute -bottom-6 -left-6 bg-gradient-to-br from-blue-500/90 to-purple-600/90 backdrop-blur-xl border border-blue-400/50 rounded-2xl px-4 py-3 shadow-[0_0_30px_rgba(59,130,246,0.4)] animate-float" style={{ animationDelay: '1s' }}>
                <div className="flex items-center gap-2">
                  <i className="ri-calendar-check-fill text-white text-xl" />
                  <div>
                    <div className="text-white font-bold text-sm whitespace-nowrap">Job Booked</div>
                    <div className="text-white/80 text-xs">Just now</div>
                  </div>
                </div>
              </div>

              <div className="absolute top-1/2 -right-12 bg-gradient-to-br from-cyan-400/90 to-blue-600/90 backdrop-blur-xl border border-cyan-400/50 rounded-2xl px-4 py-3 shadow-[0_0_30px_rgba(0,212,255,0.4)] animate-float" style={{ animationDelay: '2s' }}>
                <div className="flex items-center gap-2">
                  <i className="ri-robot-2-fill text-white text-xl" />
                  <div>
                    <div className="text-white font-bold text-sm whitespace-nowrap">AI Responding</div>
                    <div className="text-white/80 text-xs">Active</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-10 left-1/2 -translate-x-1/2 animate-bounce">
        <i className="ri-arrow-down-line text-cyan-400 text-2xl" />
      </div>
    </section>
  );
}